import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function LoginPage() {
  const { user, booting, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!booting && user) return <Navigate to="/overview" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      window.location.href = "/overview";
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign-in failed - check your email and password.");
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 bg-[--shell-0] shell-noise"
      style={{
        backgroundImage:
          "radial-gradient(1200px 600px at 20% 0%, rgba(176,18,18,0.18), transparent 55%), radial-gradient(900px 500px at 90% 10%, rgba(42,167,161,0.10), transparent 60%)",
      }}
      data-testid="login-page"
    >
      <div className="relative z-10 w-full max-w-4xl grid md:grid-cols-[1fr_420px] gap-10 items-center">
        {/* Brand block */}
        <div className="hidden md:block">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-[#F5F7FA] rounded-lg px-2 py-1.5">
              <img src="/six-logo.jpg" alt="SIX" className="h-8 w-auto" />
            </div>
            <div>
              <div className="font-display text-xl font-semibold text-[--tx-1] leading-none">SIX</div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[--tx-3] mt-1">Transformation Center</div>
            </div>
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-[--tx-1] tracking-tight leading-tight">
            The operating environment for evidence-first transformation.
          </h1>
          <p className="text-sm text-[--tx-2] leading-relaxed mt-4 max-w-md">
            Secure, multi-tenant workspaces. Deterministic analytics with full lineage.
            Senior judgment applied before anything reaches an executive register.
          </p>
          <div className="flex items-center gap-2 mt-6 text-[11px] text-[--tx-3]">
            <ShieldCheck size={14} className="text-[--green]" />
            Workspace isolation enforced server-side - client data never blends.
          </div>
        </div>

        {/* Auth card */}
        <Card className="bg-[--shell-1] border-[--hairline] shadow-2xl" data-testid="login-card">
          <CardContent className="p-7">
            <div className="md:hidden flex items-center gap-2.5 mb-6">
              <div className="bg-[#F5F7FA] rounded-lg px-1.5 py-1">
                <img src="/six-logo.jpg" alt="SIX" className="h-6 w-auto" />
              </div>
              <div>
                <div className="font-display text-sm font-semibold text-[--tx-1] leading-none">SIX</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mt-0.5">Transformation Center</div>
              </div>
            </div>
            <div className="font-display text-lg font-semibold text-[--tx-1]">Sign in</div>
            <p className="text-xs text-[--tx-3] mt-1 mb-6">Access is provisioned by your SIX engagement team.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="login-email" className="text-xs text-[--tx-2]">Email</Label>
                <Input id="login-email" type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-10"
                  data-testid="login-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="login-password" className="text-xs text-[--tx-2]">Password</Label>
                <Input id="login-password" type="password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-10"
                  data-testid="login-password-input" />
              </div>
              {error && (
                <div className="rounded-lg border border-[--danger]/40 bg-[--danger-soft] px-3 py-2 text-xs text-[--danger]" data-testid="login-error-message">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={busy}
                className="w-full h-10 bg-[--six-crimson] hover:bg-[--six-crimson-hover] text-white font-semibold active:scale-[0.98]"
                data-testid="login-submit-button">
                {busy ? (<><Loader2 size={15} className="animate-spin mr-2" /> Signing in...</>) : "Sign in"}
              </Button>
            </form>
            <div className="mt-6 pt-4 border-t border-[--divider] text-[10px] text-[--tx-3] leading-relaxed">
              Sessions expire after 12 hours. All activity is written to the center audit log.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
